import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import {
  Church,
  Envelope,
  MapPin,
  PencilSimpleLine,
  Phone,
  X
} from "@phosphor-icons/react";
import {
  GoogleMap,
  Loading,
  PhoneInputField,
  Select,
  TextField
} from "components/UI";
import { DateField } from "components/UI/InputFields/DateField";
import { MutationError } from "components/UI/MutationError";
import { GOOGLE_API_KEY } from "config";
import {
  useDenominations,
  useUpdateDenominationChurch
} from "features/denominations";
import {
  ADDRESSES,
  useCities,
  useCountries,
  useRegions,
  useSubcities,
  useWoredas,
  useZones
} from "features/members";
import { pick } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { usePlacesWidget } from "react-google-autocomplete";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useUpdateChurch } from "../api";
import { updateChurchSchema } from "../validation";

export const BasicInformation = ({ church, isDenominationChurch = false }) => {
  const [isEditMode, setIsEditMode] = useState(false);

  const churchUpdateMutation = isDenominationChurch
    ? useUpdateDenominationChurch()
    : useUpdateChurch();
  const denominationsQuery = useDenominations();
  const countriesQuery = useCountries();

  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    control
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      established_date: new Date(),
      facebook_link: "",
      instagram_link: "",
      youtube_link: "",
      x_link: "",
      website: "",
      country_id: "",
      region_id: "",
      zone_id: "",
      city_id: "",
      sub_city_id: "",
      woreda_id: "",
      kebele: "",
      house_number: "",
      specific_location: "",
      denomination_id: ""
    },
    resolver: yupResolver(updateChurchSchema)
  });

  // Set form values when church data is loaded
  useEffect(() => {
    if (church) {
      const formValues = pick(church, [
        "name",
        "email",
        "phone",
        "facebook_link",
        "instagram_link",
        "youtube_link",
        "x_link",
        "website",
        "country_id",
        "region_id",
        "zone_id",
        "city_id",
        "sub_city_id",
        "woreda_id",
        "kebele",
        "house_number",
        "specific_location",
        "denomination_id"
      ]);

      // Handle established_date specially
      formValues.established_date = formValues.established_date
        ? moment(formValues.established_date).toDate()
        : new Date();

      // Convert IDs to strings for selects
      formValues.country_id = String(formValues.country_id || "");
      formValues.region_id = String(formValues.region_id || "");
      formValues.zone_id = String(formValues.zone_id || "");
      formValues.city_id = String(formValues.city_id || "");
      formValues.sub_city_id = String(formValues.sub_city_id || "");
      formValues.woreda_id = String(formValues.woreda_id || "");
      formValues.denomination_id = String(formValues.denomination_id || "");

      reset(formValues);
    }
  }, [church, reset]);

  // Address queries
  const regionsQuery = useRegions({
    countryId: watch("country_id"),
    config: { enabled: !!watch("country_id") }
  });

  const zonesQuery = useZones({
    regionId: watch("region_id"),
    config: {
      enabled:
        !!watch("region_id") &&
        String(watch("region_id")) !== ADDRESSES.ADDIS_ABABA.REGION_ID
    }
  });

  const citiesQuery = useCities({
    zoneId: watch("zone_id"),
    config: {
      enabled:
        !!watch("zone_id") &&
        String(watch("region_id")) !== ADDRESSES.ADDIS_ABABA.REGION_ID
    }
  });

  const subcitiesQuery = useSubcities({
    cityId: watch("city_id"),
    config: {
      enabled:
        (!!watch("city_id") &&
          String(watch("region_id")) !== ADDRESSES.ADDIS_ABABA.REGION_ID) ||
        String(watch("region_id")) === ADDRESSES.ADDIS_ABABA.REGION_ID
    }
  });

  const woredasQuery = useWoredas({
    subcityId: watch("sub_city_id"),
    config: { enabled: !!watch("sub_city_id") }
  });

  const { ref: placesRef } = usePlacesWidget({
    apiKey: GOOGLE_API_KEY,
    onPlaceSelected: place => {
      setValue("latitude", place.geometry.location.lat());
      setValue("longitude", place.geometry.location.lng());
    },
    options: {
      types: ["establishment"],
      componentRestrictions: { country: "et" }
    }
  });

  const onSubmit = values => {
    const formData = new FormData();

    const processedValues = {
      ...values,
      established_date: moment(values.established_date).format("YYYY-MM-DD")
    };

    for (const key in processedValues) {
      if (processedValues[key]) {
        formData.append(key, processedValues[key]);
      }
    }

    churchUpdateMutation.mutate(
      {
        churchData: formData,
        churchId: church?.id,
        denominationId: isDenominationChurch ? church?.denomination_id : null
      },
      {
        onSuccess: () => {
          setIsEditMode(false);
        }
      }
    );
  };

  // Helper function to get specific location for Google Maps
  const getSpecificLocation = () => {
    const formValues = watch();
    return formValues.specific_location || "";
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      // Cancel edit - reset form to original values
      if (church) {
        const formValues = pick(church, [
          "name",
          "email",
          "phone",
          "facebook_link",
          "instagram_link",
          "youtube_link",
          "x_link",
          "website",
          "country_id",
          "region_id",
          "zone_id",
          "city_id",
          "sub_city_id",
          "woreda_id",
          "kebele",
          "house_number",
          "specific_location",
          "denomination_id"
        ]);

        formValues.established_date = formValues.established_date
          ? moment(formValues.established_date).toDate()
          : new Date();

        formValues.country_id = String(formValues.country_id || "");
        formValues.region_id = String(formValues.region_id || "");
        formValues.zone_id = String(formValues.zone_id || "");
        formValues.city_id = String(formValues.city_id || "");
        formValues.sub_city_id = String(formValues.sub_city_id || "");
        formValues.woreda_id = String(formValues.woreda_id || "");
        formValues.denomination_id = String(formValues.denomination_id || "");

        reset(formValues);
      }
    }
    setIsEditMode(!isEditMode);
  };

  if (countriesQuery.isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center">
        <Loading />
      </Stack>
    );
  }

  if (countriesQuery.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          {t("churches.profile.errorLoadingChurchData")}
        </Typography>
      </Box>
    );
  }

  if (!church) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t("churches.profile.noChurchDataFound")}</Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        {/* Edit Profile Button */}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          {isEditMode ? (
            <>
              <Button
                variant="outlined"
                startIcon={<X />}
                onClick={handleEditToggle}
                disabled={churchUpdateMutation.isLoading}
              >
                {t("churches.profile.cancel")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<PencilSimpleLine />}
                disabled={churchUpdateMutation.isLoading}
              >
                {t("churches.profile.saveChanges")}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<PencilSimpleLine />}
              onClick={handleEditToggle}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" }
              }}
            >
              {t("members.profile.EditProfile")}
            </Button>
          )}
        </Stack>

        {/* Church Information */}
        <Card
          sx={{
            borderRadius: "20px",
            border: "1px solid #e9eaec"
          }}
          elevation={0}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 3 }}
            >
              <Church size={24} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t("churches.profile.churchInformation")}
              </Typography>
            </Stack>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <TextField
                  label={t("churches.createChurch.churchName")}
                  name="name"
                  register={register}
                  error={!!errors.name?.message}
                  helperText={errors.name?.message}
                  disabled={!isEditMode}
                  neutral={1}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="established_date"
                  control={control}
                  render={({ field }) => (
                    <DateField
                      {...field}
                      label={t("churches.createChurch.establishedDate")}
                      name="established_date"
                      error={!!errors.established_date?.message}
                      helperText={errors.established_date?.message}
                      disabled={!isEditMode}
                      neutral={1}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Contact & Online Presence */}
        <Card
          sx={{
            borderRadius: "20px",
            border: "1px solid #e9eaec"
          }}
          elevation={0}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 3 }}
            >
              <Envelope size={24} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t("churches.profile.contactAndOnlinePresence")}
              </Typography>
            </Stack>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("churches.createChurch.churchEmailAddress")}
                  name="email"
                  register={register}
                  error={!!errors.email?.message}
                  helperText={errors.email?.message}
                  disabled={!isEditMode}
                  neutral={1}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <PhoneInputField
                      variant="filled"
                      label={t("churches.createChurch.churchPhone")}
                      error={!!errors.phone?.message}
                      helperText={errors.phone?.message}
                      disabled={!isEditMode}
                      neutral={1}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("churches.createChurch.churchWebsite")}
                  name="website"
                  register={register}
                  error={!!errors.website?.message}
                  helperText={errors.website?.message}
                  disabled={!isEditMode}
                  neutral={1}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Social Media Links */}
        <Card
          sx={{
            borderRadius: "20px",
            border: "1px solid #e9eaec"
          }}
          elevation={0}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 3 }}
            >
              <Phone size={24} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t("churches.createChurch.churchSocialMedia")}
              </Typography>
            </Stack>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("churches.createChurch.facebookLink")}
                  name="facebook_link"
                  register={register}
                  error={!!errors.facebook_link?.message}
                  helperText={errors.facebook_link?.message}
                  disabled={!isEditMode}
                  neutral={1}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("churches.createChurch.instagramLink")}
                  name="instagram_link"
                  register={register}
                  error={!!errors.instagram_link?.message}
                  helperText={errors.instagram_link?.message}
                  disabled={!isEditMode}
                  neutral={1}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("churches.createChurch.youtubeLink")}
                  name="youtube_link"
                  register={register}
                  error={!!errors.youtube_link?.message}
                  helperText={errors.youtube_link?.message}
                  disabled={!isEditMode}
                  neutral={1}
                />
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <TextField
                  label={t("churches.createChurch.xLink")}
                  name="x_link"
                  register={register}
                  error={!!errors.x_link?.message}
                  helperText={errors.x_link?.message}
                  disabled={!isEditMode}
                  neutral={1}
                />
              </Grid> */}
            </Grid>
          </CardContent>
        </Card>

        {/* Location Details */}
        <Card
          sx={{
            borderRadius: "20px",
            border: "1px solid #e9eaec"
          }}
          elevation={0}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 3 }}
            >
              <MapPin size={24} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t("churches.profile.locationDetails")}
              </Typography>
            </Stack>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Select
                  neutral={1}
                  label={t("churches.createChurch.country")}
                  name="country_id"
                  options={
                    countriesQuery.data?.result?.map(country => ({
                      optionLabel: country?.name,
                      value: country?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.country_id?.message}
                  helperText={errors.country_id?.message}
                  disabled={!isEditMode}
                  onChange={e => {
                    setValue("country_id", e.target.value, {
                      shouldValidate: true
                    });
                    setValue("region_id", "", { shouldValidate: true });
                    setValue("zone_id", "", { shouldValidate: true });
                    setValue("city_id", "", { shouldValidate: true });
                    setValue("sub_city_id", "", { shouldValidate: true });
                    setValue("woreda_id", "", { shouldValidate: true });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  neutral={1}
                  label={t("churches.createChurch.region")}
                  name="region_id"
                  options={
                    regionsQuery.data?.result?.map(region => ({
                      optionLabel: region?.name,
                      value: region?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.region_id?.message}
                  helperText={errors.region_id?.message}
                  disabled={!isEditMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  neutral={1}
                  label={t("churches.createChurch.zone")}
                  name="zone_id"
                  options={
                    zonesQuery.data?.result?.map(zone => ({
                      optionLabel: zone?.name,
                      value: zone?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.zone_id?.message}
                  helperText={errors.zone_id?.message}
                  disabled={!isEditMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  neutral={1}
                  label={t("churches.createChurch.city")}
                  name="city_id"
                  options={
                    citiesQuery.data?.result?.map(city => ({
                      optionLabel: city?.name,
                      value: city?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.city_id?.message}
                  helperText={errors.city_id?.message}
                  disabled={!isEditMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  neutral={1}
                  label={t("churches.createChurch.subcity")}
                  name="sub_city_id"
                  options={
                    subcitiesQuery.data?.result?.map(subcity => ({
                      optionLabel: subcity?.name,
                      value: subcity?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.sub_city_id?.message}
                  helperText={errors.sub_city_id?.message}
                  disabled={!isEditMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  neutral={1}
                  label={t("churches.createChurch.woreda")}
                  name="woreda_id"
                  options={
                    woredasQuery.data?.result?.map(woreda => ({
                      optionLabel: woreda?.name,
                      value: woreda?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.woreda_id?.message}
                  helperText={errors.woreda_id?.message}
                  disabled={!isEditMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("churches.createChurch.kebele")}
                  name="kebele"
                  register={register}
                  error={!!errors.kebele?.message}
                  helperText={errors.kebele?.message}
                  disabled={!isEditMode}
                  neutral={1}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("churches.createChurch.houseNumber")}
                  name="house_number"
                  register={register}
                  error={!!errors.house_number?.message}
                  helperText={errors.house_number?.message}
                  disabled={!isEditMode}
                  neutral={1}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("churches.createChurch.specificLocation")}
                  name="specific_location"
                  register={register}
                  error={!!errors.specific_location?.message}
                  helperText={errors.specific_location?.message}
                  disabled={!isEditMode}
                  neutral={1}
                  ref={placesRef}
                />
              </Grid>

              {/* Map Integration */}
              <Grid item xs={12}>
                <GoogleMap
                  address={getSpecificLocation()}
                  churchName={watch("name") || church?.name}
                  height={300}
                  zoom={15}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Denomination Information */}
        <Card
          sx={{
            borderRadius: "20px",
            border: "1px solid #e9eaec"
          }}
          elevation={0}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 3 }}
            >
              <Church size={24} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t("churches.profile.denominationInformation")}
              </Typography>
            </Stack>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Select
                  neutral={1}
                  label={t("churches.createChurch.denomination")}
                  name="denomination_id"
                  options={
                    denominationsQuery.data?.result?.map(denomination => ({
                      optionLabel: denomination?.name,
                      value: denomination?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.denomination_id?.message}
                  helperText={errors.denomination_id?.message}
                  disabled={!isEditMode}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Mutation Error Display */}
        <MutationError error={churchUpdateMutation.error} />
      </Stack>
    </Box>
  );
};
